import {H3Text, H5Text, H6Text} from '@buncha/components/core/text';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BUserScreens} from '@buncha/B2B/navigation/types';
import {Box} from '@buncha/components/core/view';
import Header from '@buncha/components/composites/Header/Header';
import {MAX_GRAPQHL_INT} from '@buncha/config/utils/constants';
import {MainScreens} from '@buncha/navigation/types';
import {Modules} from '@buncha/utils/types';
import OrderHeader from '../../components/orderHeader/OrderHeader';
import {PButton} from '@buncha/components/core/Button';
import {PTextInput} from '@buncha/components/core/input/TextInput';
import {encryptToBase64} from '@buncha/B2B/utils';
import font from '@buncha/appStyles/style/font';
import {navigation} from '@buncha/config/navigation';
import {setAuthParams} from '@buncha/config/axios/service';
import {styles} from './style';
import {useFetchPostData} from '@buncha/config/hooks/axios';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface RouteParams {
  orderId?: number;
}

interface LoginResponse {
  data: {
    id?: string;
    contactInfoId?: string;
    externalOrderId?: string;
    message: string;
  };
}

export const UserLogin: React.FC = () => {
  const route = useRoute();
  const routeParams: RouteParams | undefined = route?.params;
  const [orderId, setOrderId] = useState(
    routeParams?.orderId?.toString() ?? '',
  );

  const {title, body} =
    useGetLocale(Modules.B2BUserOrder, B2BUserScreens.UserLogin) ?? {};
  const [isOrderIdHovered, setIsOrderIdHovered] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneNumberHovered, setIsPhoneNumberHovered] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');

  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const mortiseFont = useFontTheme('mortise');
  const [login, data, loading, error] = useFetchPostData<LoginResponse>();

  const handleOrderIdChange = useCallback((val: string) => {
    if (
      (!Number(val) && val?.length > 0) ||
      val?.includes('.') ||
      Number(val) > MAX_GRAPQHL_INT
    )
      return;
    setErrorMessage('');
    setOrderId(val);
  }, []);

  const handlePhoneChange = useCallback((val: string) => {
    if (
      (!Number(val) && val?.length > 0) ||
      val?.length > 10 ||
      val?.includes('.')
    )
      return;
    setErrorMessage('');
    setPhoneNumber(val);
  }, []);

  const disableSubmit =
    !orderId || !phoneNumber || phoneNumber?.length !== 10 || loading;

  const handleSubmit = useCallback(async () => {
    const response = await login(`/api/v1/customer/user/${orderId}/user-auth`, {
      body: {
        phoneNumber: '1' + phoneNumber,
      },
    });
    const userResponse = response?.data;
    if (userResponse)
      Analytics.setIdentifiedUserAttribute({
        id: userResponse?.id,
        contactInfoId: userResponse?.contactInfoId,
      });
  }, [login, orderId, phoneNumber]);

  const onOrderIdBlur = useCallback(() => setIsOrderIdHovered(false), []);
  const onOrderIdFocus = useCallback(() => setIsOrderIdHovered(true), []);

  const onPhoneNumnerBlur = useCallback(
    () => setIsPhoneNumberHovered(false),
    [],
  );
  const onPhoneNumnerFocus = useCallback(
    () => setIsPhoneNumberHovered(true),
    [],
  );

  const borderColor = useMemo(() => {
    if (errorMessage?.length) return theme?.text?.red?.[100];
    if (isPhoneNumberHovered || phoneNumber?.length > 0)
      return theme?.text?.blue?.[300];
    return theme?.text.gray[200];
  }, [
    errorMessage?.length,
    isPhoneNumberHovered,
    phoneNumber?.length,
    theme?.text?.blue,
    theme?.text.gray,
    theme?.text?.red,
  ]);

  useEffect(() => {
    if (data?.data?.contactInfoId?.length && data?.data?.id?.length) {
      setAuthParams(data?.data?.contactInfoId, data?.data?.id);
      navigation.navigate(MainScreens.B2B, {
        screen: B2BUserScreens.OrderInfo,
        params: {
          orderId: encryptToBase64(data?.data?.externalOrderId ?? ''),
        },
      });
    }
  }, [data]);

  useEffect(() => {
    if (error?.response?.data?.data?.message)
      setErrorMessage(error?.response?.data?.data?.message);
  }, [error?.response?.data?.data?.message]);

  return (
    <Header>
      <OrderHeader />
      <Box style={styles.mainSection}>
        <Box style={styles.section}>
          <H3Text
            style={{
              fontFamily: mortiseFont[700].normal,
              color: theme?.text.blue[300],
            }}>
            {title?.verify}
          </H3Text>
          <H6Text>{title?.enterPhone}</H6Text>
        </Box>
        <Box style={styles.section}>
          <H5Text
            style={{
              fontFamily: muliFont[400].normal,
              color: theme?.text.blue[300],
            }}>
            {body?.orderId}
          </H5Text>
          <PTextInput
            placeholder="Enter Order ID"
            debounce={false}
            textAlign="center"
            value={orderId}
            onChangeText={handleOrderIdChange}
            keyboardType="number-pad"
            returnKeyType="done"
            leftComponent={
              <H6Text
                style={[
                  styles.orderIdLeftComp,
                  {
                    color:
                      isOrderIdHovered || orderId?.length > 0
                        ? theme?.text?.blue?.[300]
                        : theme?.text.gray[100],
                    fontFamily: muliFont[400]?.normal,
                  },
                ]}>
                {body?.orderIdPrefix}
              </H6Text>
            }
            onFocus={onOrderIdFocus}
            onBlur={onOrderIdBlur}
            containerStyle={[
              styles.input,
              {
                borderColor:
                  isOrderIdHovered || orderId?.length > 0
                    ? theme?.text?.blue?.[300]
                    : theme?.text.gray[200],
              },
            ]}
            style={{
              fontFamily: muliFont[400].normal,
              ...font.FontSize.fontH5,
              color: theme?.text.blue[300],
            }}
          />
        </Box>
        <Box style={styles.section}>
          <H5Text
            style={{
              fontFamily: muliFont[400].normal,
              color: theme?.text.blue[300],
            }}>
            {body?.phoneNumber}
          </H5Text>
          <PTextInput
            placeholder="Enter Mobile"
            debounce={false}
            textAlign="center"
            value={phoneNumber}
            onChangeText={handlePhoneChange}
            keyboardType="number-pad"
            returnKeyType="done"
            leftComponent={
              <H6Text
                style={[
                  styles.phoneNumberLeftComp,
                  {
                    color:
                      isPhoneNumberHovered || phoneNumber?.length > 0
                        ? theme?.text?.blue?.[300]
                        : theme?.text.gray[100],
                    fontFamily: muliFont[400]?.normal,
                  },
                ]}>
                {body?.phoneNumberPrefix}
              </H6Text>
            }
            onFocus={onPhoneNumnerFocus}
            onBlur={onPhoneNumnerBlur}
            containerStyle={[
              styles.input,
              {
                borderColor,
              },
            ]}
            style={{
              fontFamily: muliFont[400].normal,
              ...font.FontSize.fontH5,
              color: theme?.text.blue[300],
            }}
          />
          <H6Text
            style={{
              color: theme?.text?.red?.[100],
              fontFamily: muliFont[400].normal,
            }}>
            {errorMessage}
          </H6Text>
        </Box>
        <PButton
          title={'Submit'}
          disabled={disableSubmit}
          onPress={handleSubmit}
          isLoading={disableSubmit}
          style={styles.bottomButton}
          bgColor={theme?.background.yellow[300]}
          titleColor={theme?.text?.blue?.[300]}
          titleStyle={{
            ...font.FontSize.fontH5,
          }}
          disabledBgColor={theme?.background?.gray?.[200]}
          changeStyleOnLoading
          loadingText="Submit"
          titleDisableColor={theme?.text?.blue?.[300]}
        />
        <Box>
          <H6Text
            style={[
              styles.header,
              {
                fontFamily: muliFont[700].normal,
                color: theme?.text.gray[100],
              },
            ]}>
            {title?.disclaimer}
          </H6Text>
          <H6Text
            style={[
              styles.subTitle,
              {
                fontFamily: muliFont[400].normal,
                color: theme?.text.gray[100],
              },
            ]}>
            {body?.userDisclaimer}
          </H6Text>
        </Box>
      </Box>
    </Header>
  );
};

export default React.memo(UserLogin);
